import { useState } from "react";
import PropTypes from "prop-types";
import { compareService } from "../../service/dataComparator/dataCompareService";

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "N/A");

export default function DuplicateResolutionModal({ cosmosRecords, klaviyoProfiles, customerId, hasDeleteAccess, onResolved, onDismiss }) {

    const [actionInFlight, setActionInFlight] = useState(false);

    const [error, setError] = useState("");

    const hasCosmosDuplicates = cosmosRecords.length > 1;
    const hasKlaviyoDuplicates = klaviyoProfiles.length > 1;

    if (!hasCosmosDuplicates && !hasKlaviyoDuplicates) {
        return null;
    }

    const runAction = (promise) => {
        setActionInFlight(true);
        setError("");
        promise.then((response) => {
            setActionInFlight(false);
            if (response.error) {
                setError(response.error);
                return;
            }
            onResolved();
        }, () => {
            setActionInFlight(false);
            setError("Action failed. Please try again.");
        });
    };

    const handleDeleteCosmos = (record) => {
        if (!window.confirm("Delete this duplicate Cosmos record?")) return;
        runAction(compareService.deleteBySource(record.id, "cosmos"));
    };

    const handleDeleteKlaviyo = (profile) => {
        if (!window.confirm("Delete this duplicate Klaviyo profile?")) return;
        runAction(compareService.deleteKlaviyoDuplicate(profile.profileId, customerId));
    };

    const handleMergeKlaviyo = () => {
        if (!window.confirm("Merge all duplicate Klaviyo profiles? The oldest profile will be kept and updated with newer field values.")) return;
        runAction(compareService.mergeDuplicates({ customerId, source: "klaviyo" }));
    };

    return (
        <div className="modal-overlay">
            <div className="modal-box duplicate-modal">
                <div className="modal-title">
                    <h2>
                        Duplicate Records Found
                    </h2>
                    <button
                        type="button"
                        className="close-btn"
                        onClick={onDismiss}
                        disabled={actionInFlight}
                    >
                        ✕
                    </button>

                </div>

                <div className="duplicate-content">
                    {
                        error &&
                        <div className="banner error">
                            ✖ {error}
                        </div>
                    }

                    {
                        hasCosmosDuplicates &&
                        <div className="duplicate-section">
                            <div className="duplicate-section-header">
                                <h3>
                                    Cosmos ({cosmosRecords.length} records)
                                </h3>
                            </div>
                            <table>
                                <thead>
                                <tr>
                                    <th>Id</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Loyalty ID</th>
                                    <th>Created</th>
                                    <th>Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    cosmosRecords.map((record) => (
                                        <tr key={record.id}>
                                            <td>{record.id}</td>
                                            <td>{record.email}</td>
                                            <td>{record.phoneNo}</td>
                                            <td>{record.loyaltyId}</td>
                                            <td>{formatDateTime(record.createdDate)}</td>
                                            <td>
                                                {
                                                    hasDeleteAccess &&
                                                    <button type="button" className="delete" disabled={actionInFlight}
                                                        onClick={() => handleDeleteCosmos(record)}>
                                                        Delete
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                    ))
                                }
                                </tbody>
                            </table>
                        </div>
                    }

                    {
                        hasKlaviyoDuplicates &&
                        <div className="duplicate-section">
                            <div className="duplicate-section-header">
                                <h3>
                                    Klaviyo ({klaviyoProfiles.length} profiles)
                                </h3>
                                {
                                    hasDeleteAccess &&
                                    <button type="button" className="save" disabled={actionInFlight} onClick={handleMergeKlaviyo}>
                                        Merge All (keep oldest)
                                    </button>
                                }
                            </div>
                            <table>
                                <thead>
                                <tr>
                                    <th>Profile Id</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Loyalty ID</th>
                                    <th>Created</th>
                                    <th>Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    klaviyoProfiles.map((profile) => (
                                        <tr key={profile.profileId}>
                                            <td>{profile.profileId}</td>
                                            <td>{profile.email}</td>
                                            <td>{profile.phoneNumber}</td>
                                            <td>{profile.loyaltyId}</td>
                                            <td>{formatDateTime(profile.createdAt)}</td>
                                            <td>
                                                {
                                                    hasDeleteAccess &&
                                                    <button type="button" className="delete" disabled={actionInFlight}
                                                        onClick={() => handleDeleteKlaviyo(profile)}>
                                                        Delete
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                    ))
                                }
                                </tbody>
                            </table>
                        </div>
                    }

                    <div className="footer">
                        <button type="button" className="delete" disabled={actionInFlight} onClick={onDismiss}>
                            Continue without resolving
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

DuplicateResolutionModal.propTypes = {
    cosmosRecords: PropTypes.array,
    klaviyoProfiles: PropTypes.array,
    customerId: PropTypes.string,
    hasDeleteAccess: PropTypes.bool,
    onResolved: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired
};

DuplicateResolutionModal.defaultProps = {
    cosmosRecords: [],
    klaviyoProfiles: [],
    hasDeleteAccess: false
};
